<template>
  <div class="row">
    <div class="col">
      <div
        :class="{ 'payment-term-layout': responsiveLayout }"
        v-if="!isCheckoutOnCreditLine"
      >
        <PaymentTermV2
          v-for="(offer, i) in availableOffers"
          :key="i"
          :selected="offer.months === props.selectedOffer"
          :payment-frequency="offer.paymentFrequency"
          :months="Number(offer.months)"
          :number-of-installments="offer.numberOfInstallments"
          :payment-amount="offer.paymentAmount"
          :monthly-payment="offer.monthlyPayment"
          :total-payment="offer.totalAmount"
          :currency="currency"
          :locale="merchantLocale"
          :pre-approval-state="props.preApprovalState"
          :disable-button="props.disableOfferButton"
          :badge="calcBadge(i, offer)"
          @on-select="$emit('onSelect', $event)"
        />
      </div>
      <div
        :class="{ 'payment-term-layout': responsiveLayout }"
        v-if="isCheckoutOnCreditLine"
      >
        <PaymentCreditLineEstimate
          v-for="(offer, i) in availableOffers"
          :key="i"
          :selected="offer.months === props.selectedOffer"
          :currency="currency"
          :new-minimum-payment-amount="
            offer?.creditLineEstimate?.newMinimumPaymentAmount
          "
          :minimum-payment-amount-for-simulated-debit="
            offer?.creditLineEstimate?.minimumPaymentAmountForSimulatedDebit
          "
          :current-minimum-payment-amount="
            offer?.creditLineEstimate?.currentMinimumPaymentAmount
          "
          :totalPhonePrice="offer?.creditLineEstimate?.totalPhonePrice"
          :weeks="offer.weeks"
          :months="Number(offer.months)"
          @onSelect="$emit('onSelect', $event)"
        />
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.payment-term-layout {
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
}
@media (max-width: 768px) {
  .payment-term-layout {
    flex-direction: column;
  }
}
</style>
<script setup lang="ts">
import PaymentTermV2 from "@/apps/merchant3/components/offers/payment-terms/PaymentTermV2.vue";
import { computed, ComputedRef, onMounted, PropType, ref } from "vue";
import { useStore } from "vuex";
import {
  IBadge,
  IBadgeExperiments,
  IPaymentTerms,
} from "@/apps/merchant3/models/IStateCart";
import ActionLogs from "@/apps/merchant3/types/ActionLogEvents";
import logAction from "@/apps/merchant3/services/action-logs.service";
import useCurrency from "@/apps/merchant3/shared-hooks/useCurrency";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import { ApprovalState } from "@/apps/merchant3/types/OBR";
import PaymentCreditLineEstimate from "@/apps/merchant3/components/offers/PaymentCreditLineEstimate.vue";
import { isCheckoutOnCreditLine } from "@/apps/merchant3/helpers/cartActiveFeatures.helper";
import { isPhilippinesSaleFlow } from "@/apps/merchant3/helpers/checkoutFlows";
import useLocale from "@/apps/merchant3/shared-hooks/useLocale";

const props = defineProps({
  disableOfferButton: { type: Boolean },
  selectedOffer: { type: Number },
  preApprovalState: {
    type: String as PropType<ApprovalState>,
    default: ApprovalState.PENDING,
  },
  useResponsiveLayout: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const hasAccessories = computed(() => {
  return (
    store.state.offers.selectedCartAccessory?.type != undefined &&
    store.state.offers.selectedCartAccessory?.type != ""
  );
});

const availableOffers = computed(() => {
  if (hasAccessories.value && isPhilippinesSaleFlow()) {
    return {
      9: store.state.carts.pricing[9],
    };
  }
  return store.state.carts.pricing;
});

const maxMonths = ref();
const deviceModelId = computed(() => {
  return store.state.offers.selectedDeviceModelObject?.deviceModelId;
});

const responsiveLayout: ComputedRef<boolean> = computed(() => {
  return props.useResponsiveLayout;
});
const experiment = computed(() => {
  const experiments = store.state.carts?.experiments;
  if (experiments?.lowerWeeklyPaymentBadges) {
    return IBadgeExperiments.LOWER_WEEKLY_PAYMENT_BADGES;
  } else if (experiments?.monthTermPopularityBadges) {
    return IBadgeExperiments.MONTH_TERM_POPULARITY_BADGES;
  } else if (experiments?.lowestTotalPaymentBadge) {
    return IBadgeExperiments.LOWEST_TOTAL_PAYMENT_BADGE;
  } else return IBadgeExperiments.NO_PAYMENT_BADGE;
});
const { getCurrency } = useCurrency();
const { getLocale } = useLocale();

const currency = computed(() => {
  return getCurrency(store.state.merchant?.merchant?.country);
});
const merchantLocale = computed(() => {
  return store.state.merchant?.merchant?.country
    ? getLocale(store.state.merchant.merchant.country).localeWeb
    : "es-MX";
});

const logBadgesActionLog = (eventType, eventName = "offerDisplayed") => {
  logAction(eventType, {
    eventName: eventName,
    deviceModelId: deviceModelId.value,
    devicePrice: store.state.carts?.priceWithSalesTax,
    minDownPayment:
      store.state.offers?.selectedDeviceModelObject?.minDownPayment,
    termsOptions: paymentTerms,
  });
};

let paymentTerms: IPaymentTerms = availableOffers.value;
const calcBadge = (index, offer) => {
  let badge: IBadge = {
    badgeType: IBadgeExperiments.NO_PAYMENT_BADGE,
    badgeValue: 0,
  };

  const [lowestWeeklyPayment, maxWeeklyPayment] = getMinMaxWeeklyPayment();
  const maxPopularityValue = getMaxPopularityValue();
  let popularityValue = "";
  const totalSaving = getTotalSaving();

  switch (experiment.value) {
    case IBadgeExperiments.LOWER_WEEKLY_PAYMENT_BADGES:
      if (offer.weeklyPayment !== maxWeeklyPayment) {
        if (parseInt(index) === offer.months) {
          badge.badgeType = IBadgeExperiments.LOWER_WEEKLY_PAYMENT_BADGES;
          badge.badgeValue = Math.round(maxWeeklyPayment - offer.weeklyPayment);
          paymentTerms[index].lowerVal = badge.badgeValue;
          paymentTerms[index].best =
            offer.weeklyPayment === lowestWeeklyPayment ? 1 : 0;
        }
      } else {
        paymentTerms[index].best = null;
      }
      break;
    case IBadgeExperiments.NO_PAYMENT_BADGE:
      logEvent(EventLogs.Events.TERM_OPTIONS_NO_PAYMENT_BADGE, {
        index: index,
        paymentTerms: paymentTerms,
      });
      break;
    case IBadgeExperiments.MONTH_TERM_POPULARITY_BADGES:
      if (paymentTerms[index].popularity) {
        badge.badgeType = IBadgeExperiments.MONTH_TERM_POPULARITY_BADGES;
        badge.badgeValue = paymentTerms[index].popularity;
        popularityValue = paymentTerms[index].popularity;
        popularityValue.replace("+", "");
        if (parseInt(popularityValue) === maxPopularityValue) {
          paymentTerms[index].best = 1;
        } else {
          paymentTerms[index].best = 0;
        }
      } else {
        paymentTerms[index].best = 0;
      }
      break;
    case IBadgeExperiments.LOWEST_TOTAL_PAYMENT_BADGE:
      //shortest term
      if (parseInt(index) === 3) {
        badge.badgeType = IBadgeExperiments.LOWEST_TOTAL_PAYMENT_BADGE;
        badge.badgeValue = totalSaving;
        paymentTerms[index].best = 1;
      } else if (paymentTerms[index]) {
        paymentTerms[index].best = 0;
      }
      break;
  }
  return badge;
};

const getMinMaxWeeklyPayment = () => {
  const result: number[] = [];
  const resultMonth: number[] = [];
  for (const [, value] of Object.entries(availableOffers.value)) {
    const offerList = JSON.parse(JSON.stringify(value));
    result.push(offerList.weeklyPayment);
    resultMonth.push(offerList.months);
  }
  maxMonths.value = Math.max(...resultMonth);
  const min = Math.min(...result);
  const max = Math.max(...result);
  return [min, max];
};

const getMaxPopularityValue = () => {
  const result: number[] = [];
  for (const [, value] of Object.entries(availableOffers.value)) {
    const offerList = JSON.parse(JSON.stringify(value));
    if (offerList.popularity) {
      offerList.popularity.replace("+", "");
      result.push(parseInt(offerList.popularity));
    }
  }
  return Math.max(...result);
};

const getTotalSaving = () => {
  const result: number[] = [];
  for (const [, value] of Object.entries(availableOffers.value)) {
    const offerList = JSON.parse(JSON.stringify(value));
    result.push(offerList.totalAmount);
  }
  const max = Math.max(...result);
  const min = Math.min(...result);

  return Number((max - min).toFixed(2));
};

onMounted(async () => {
  switch (experiment.value) {
    case IBadgeExperiments.LOWER_WEEKLY_PAYMENT_BADGES:
      await logEvent(EventLogs.Events.TERM_OPTIONS_AMOUNT_LOWER_PAYMENT_BADGE);
      logBadgesActionLog(
        ActionLogs.Events.TERM_OPTIONS_LOWER_WEEKLY_PAYMENT_BADGES
      );
      break;
    case IBadgeExperiments.MONTH_TERM_POPULARITY_BADGES:
      logBadgesActionLog(
        ActionLogs.Events.TERM_OPTIONS_MONTH_TERM_POPULARITY_BADGES
      );
      break;
    case IBadgeExperiments.LOWEST_TOTAL_PAYMENT_BADGE:
      logBadgesActionLog(
        ActionLogs.Events.TERM_OPTIONS_LOWEST_TOTAL_PAYMENT_BADGE
      );
      break;
    default:
      await logEvent(EventLogs.Events.TERM_OPTIONS_NO_PAYMENT_BADGE);
      logBadgesActionLog(ActionLogs.Events.TERM_OPTIONS_NO_PAYMENT_BADGE);
  }
});
</script>
